import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Camera, Film, Upload as UploadIcon } from "lucide-react";

const empty = {
  submitter_name: "",
  submitter_email: "",
  title: "",
  caption: "",
  video_url: "",
};

const FanSubmissionModal = ({ open, onClose }) => {
  const [tab, setTab] = useState("photo");
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const reset = () => {
    setForm(empty);
    setFile(null);
    setDone(false);
    setTab("photo");
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.submitter_name) {
      toast.error("Please tell us your name");
      return;
    }
    if (tab === "photo" && !file) {
      toast.error("Pick a photo to share");
      return;
    }
    if (tab === "video" && !form.video_url) {
      toast.error("Paste a YouTube, Vimeo or Shorts link");
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("submitter_name", form.submitter_name);
      if (form.submitter_email) fd.append("submitter_email", form.submitter_email);
      if (form.title) fd.append("title", form.title);
      if (form.caption) fd.append("caption", form.caption);
      if (tab === "photo" && file) fd.append("file", file);
      if (tab === "video" && form.video_url) fd.append("video_url", form.video_url);

      await api.post("/media-submissions", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(true);
      toast.success("Sent! Seth will review it.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6 overflow-y-auto"
      onClick={close}
      data-testid="fan-submit-modal"
    >
      <div
        className="bg-neutral-950 border border-white/15 w-full md:max-w-2xl max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-white/10">
          <div>
            <p className="eyebrow">Fan Submission</p>
            <h2 className="font-heading text-2xl md:text-3xl uppercase mt-1">Share Your Shot</h2>
          </div>
          <button onClick={close} className="text-neutral-400 hover:text-white" aria-label="Close" data-testid="fan-submit-close">
            <X size={22} />
          </button>
        </div>

        {done ? (
          <div className="px-6 md:px-10 py-12 text-center" data-testid="fan-submit-success">
            <p className="eyebrow">Thanks · Submitted</p>
            <h3 className="font-heading text-4xl uppercase mt-3 leading-tight">In Seth&apos;s inbox</h3>
            <p className="text-neutral-400 mt-4 max-w-md mx-auto">
              He&apos;ll approve it from his email. If it makes the cut, you&apos;ll see it on the Media page within a day or two.
            </p>
            <div className="mt-8 flex gap-3 justify-center">
              <button onClick={reset} className="btn-retro" data-testid="fan-submit-another">Submit another</button>
              <button onClick={close} className="btn-retro-solid" data-testid="fan-submit-done">Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 md:px-10 py-8 space-y-8" data-testid="fan-submit-form">
            <p className="text-sm text-neutral-400 leading-relaxed">
              Got a great shot or clip of us live? Send it over. Seth reviews everything before it goes up — keeps the gallery looking right.
            </p>

            <div className="flex border border-white/15">
              <button type="button" onClick={() => setTab("photo")}
                className={`flex-1 py-3 text-xs font-mono tracking-[0.25em] uppercase flex items-center justify-center gap-2 transition-colors ${tab === "photo" ? "bg-white text-black" : "text-neutral-400 hover:text-white"}`}
                data-testid="fan-tab-photo">
                <Camera size={14}/> Photo
              </button>
              <button type="button" onClick={() => setTab("video")}
                className={`flex-1 py-3 text-xs font-mono tracking-[0.25em] uppercase flex items-center justify-center gap-2 transition-colors ${tab === "video" ? "bg-white text-black" : "text-neutral-400 hover:text-white"}`}
                data-testid="fan-tab-video">
                <Film size={14}/> Video link
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label className="label-retro">Your name *</label>
                <input value={form.submitter_name} onChange={onChange("submitter_name")} className="field" required data-testid="fan-name" />
              </div>
              <div>
                <label className="label-retro">Your email (optional)</label>
                <input type="email" value={form.submitter_email} onChange={onChange("submitter_email")} className="field" placeholder="So Seth can credit you" data-testid="fan-email" />
              </div>
            </div>

            <div>
              <label className="label-retro">Title</label>
              <input value={form.title} onChange={onChange("title")} className="field" placeholder={`e.g. "Late set at Patrick Molloy's"`} data-testid="fan-title" />
            </div>

            {tab === "photo" ? (
              <div>
                <label className="label-retro">Photo file *</label>
                <label htmlFor="fan-file" className="block cursor-pointer border-2 border-dashed border-white/30 hover:border-white transition-colors py-8 px-4 text-center">
                  <UploadIcon className="mx-auto mb-3" size={26} />
                  {file ? (
                    <>
                      <p className="font-mono text-sm">{file.name}</p>
                      <p className="text-xs text-neutral-500 mt-1">{(file.size/1024/1024).toFixed(2)} MB · tap to change</p>
                    </>
                  ) : (
                    <>
                      <p className="font-heading text-xl uppercase">Tap to pick a photo</p>
                      <p className="text-xs text-neutral-500 mt-1 font-mono tracking-[0.2em] uppercase">JPEG · PNG · HEIC · max 12 MB</p>
                    </>
                  )}
                  <input
                    id="fan-file"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    data-testid="fan-file"
                  />
                </label>
              </div>
            ) : (
              <div>
                <label className="label-retro">Video link *</label>
                <input value={form.video_url} onChange={onChange("video_url")} className="field" placeholder="https://www.youtube.com/watch?v=..." data-testid="fan-video-url" />
                <p className="text-xs text-neutral-500 mt-2 font-mono">YouTube, YouTube Shorts, or Vimeo. We&apos;ll embed it.</p>
              </div>
            )}

            <div>
              <label className="label-retro">Caption / note (optional)</label>
              <textarea value={form.caption} onChange={onChange("caption")} className="field-area" placeholder="When + where you got the shot" data-testid="fan-caption" />
            </div>

            <div className="border-t border-white/10 pt-6 flex items-center justify-between flex-wrap gap-4">
              <p className="font-mono text-[10px] text-neutral-500 max-w-sm">
                Seth approves submissions from his email. Nothing goes live without his sign-off.
              </p>
              <button disabled={submitting} type="submit" className="btn-retro-solid disabled:opacity-50" data-testid="fan-submit-button">
                {submitting ? "Sending..." : "Send for Approval"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default FanSubmissionModal;
